import * as React from "react";
import { ChevronLeft, Loader2, Plus, Zap } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { WordService } from "../../services/wordService";
import type { WorkPillExecution } from "../../../types/navigationTypes";
import type { WorkPillForWizard, SelectedTemplateWithConfig, OrgTemplateRow } from "../../../features/projects/create-project-wizard/wizardTypes";
import {
  fetchWorkpillsForWizard,
  fetchOrganizationTemplates,
  fetchOrganizationKnowledgeBases,
  validateSelectedTemplatesHaveFiles,
} from "../../../features/projects/create-project-wizard/wizardQueries";
import { createProjectWithWizardResources } from "../../../features/projects/create-project-wizard/wizardMutations";
import { WizardResourcesForm } from "./WizardResourcesForm";
import { TemplateConfigDialog } from "./TemplateConfigDialog";

export type CreateWizardInitialMode = "blank" | "workpill";

interface CreateProjectWizardModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialMode: CreateWizardInitialMode;
  organizationId: string | null;
  userId: string | null;
  onCreated: (execution: WorkPillExecution) => void | Promise<void>;
}

function seedTemplatesFromWorkpill(wp: WorkPillForWizard): SelectedTemplateWithConfig[] {
  return (wp.linkedTemplates || []).map((t) => ({
    id: t.id,
    name: t.name,
    type: t.type ?? undefined,
    fieldValues: {},
    blocks: [],
    slotCount: 4,
  }));
}

export const CreateProjectWizardModal: React.FC<CreateProjectWizardModalProps> = ({
  open,
  onOpenChange,
  initialMode,
  organizationId,
  userId,
  onCreated,
}) => {
  const [mode, setMode] = React.useState<CreateWizardInitialMode>(initialMode);
  const [workpillPhase, setWorkpillPhase] = React.useState<"pick" | "configure">(
    initialMode === "workpill" ? "pick" : "pick"
  );
  const [executionId, setExecutionId] = React.useState("");

  const [workpills, setWorkpills] = React.useState<WorkPillForWizard[]>([]);
  const [orgTemplates, setOrgTemplates] = React.useState<OrgTemplateRow[]>([]);
  const [orgKbs, setOrgKbs] = React.useState<{ id: string; name: string; is_global?: boolean | null }[]>([]);
  const [loadingData, setLoadingData] = React.useState(false);

  const [blankProjectName, setBlankProjectName] = React.useState("");
  const [projectName, setProjectName] = React.useState("");
  const [selectedWorkpillId, setSelectedWorkpillId] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");

  const [selectedTemplatesWithConfig, setSelectedTemplatesWithConfig] = React.useState<SelectedTemplateWithConfig[]>([]);
  const [selectedKnowledgeBaseIds, setSelectedKnowledgeBaseIds] = React.useState<string[]>([]);
  const [uploadedTemplateFiles, setUploadedTemplateFiles] = React.useState<File[]>([]);

  const [templateConfigTarget, setTemplateConfigTarget] = React.useState<SelectedTemplateWithConfig | null>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);

  const selectedWorkpill = React.useMemo(() => {
    if (mode !== "workpill" || workpillPhase === "pick") return null;
    return workpills.find((w) => w.id === selectedWorkpillId) ?? null;
  }, [mode, workpillPhase, workpills, selectedWorkpillId]);

  const filteredWorkpills = React.useMemo(() => {
    if (!searchQuery.trim()) return workpills;
    const q = searchQuery.toLowerCase().trim();
    return workpills.filter(
      (wp) =>
        wp.name.toLowerCase().includes(q) ||
        (wp.description && wp.description.toLowerCase().includes(q)) ||
        wp.linkedLabels?.some((l) => l.name.toLowerCase().includes(q))
    );
  }, [workpills, searchQuery]);

  React.useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setWorkpillPhase(initialMode === "workpill" ? "pick" : "pick");
    setBlankProjectName("");
    setProjectName("");
    setSelectedWorkpillId("");
    setSearchQuery("");
    setSelectedTemplatesWithConfig([]);
    setSelectedKnowledgeBaseIds([]);
    setUploadedTemplateFiles([]);
    setExecutionId(crypto.randomUUID());
    setTemplateConfigTarget(null);
  }, [open, initialMode]);

  React.useEffect(() => {
    if (!open || !organizationId || !userId) {
      return () => {};
    }
    let cancelled = false;
    (async () => {
      setLoadingData(true);
      try {
        const [wp, tpl, kbs] = await Promise.all([
          fetchWorkpillsForWizard(),
          fetchOrganizationTemplates(organizationId),
          fetchOrganizationKnowledgeBases(organizationId),
        ]);
        if (!cancelled) {
          setWorkpills(wp);
          setOrgTemplates(tpl);
          setOrgKbs(kbs);
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(e instanceof Error ? e.message : "Failed to load wizard data");
        }
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, organizationId, userId]);

  const seedFromWorkpill = React.useCallback((wp: WorkPillForWizard) => {
    setSelectedTemplatesWithConfig(seedTemplatesFromWorkpill(wp));
    setSelectedKnowledgeBaseIds((wp.linkedKBs || []).map((k) => k.id));
    setUploadedTemplateFiles([]);
  }, []);

  const lastSeededRef = React.useRef("");
  React.useEffect(() => {
    if (!open || mode !== "workpill" || workpillPhase !== "configure") return;
    const wp = selectedWorkpill;
    const id = wp?.id ?? "";
    if (!wp || id === lastSeededRef.current) return;
    lastSeededRef.current = id;
    seedFromWorkpill(wp);
  }, [open, mode, workpillPhase, selectedWorkpill, seedFromWorkpill]);

  React.useEffect(() => {
    if (!open || mode !== "workpill" || workpillPhase === "pick") {
      lastSeededRef.current = "";
    }
  }, [open, mode, workpillPhase]);

  const toggleTemplate = React.useCallback((t: OrgTemplateRow, add: boolean) => {
    setSelectedTemplatesWithConfig((prev) => {
      if (add) {
        if (prev.some((p) => p.id === t.id)) return prev;
        return [
          ...prev,
          {
            id: t.id,
            name: t.name,
            type: t.type ?? undefined,
            fieldValues: {},
            blocks: [],
            slotCount: 4,
          },
        ];
      }
      return prev.filter((p) => p.id !== t.id);
    });
  }, []);

  const toggleKb = React.useCallback((id: string, add: boolean) => {
    setSelectedKnowledgeBaseIds((prev) =>
      add ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id)
    );
  }, []);

  const isCreateDisabled = React.useMemo(() => {
    if (!userId || !organizationId || loadingData || isProcessing) return true;
    if (mode === "blank") {
      return !blankProjectName.trim();
    }
    if (workpillPhase === "pick" || !selectedWorkpill) return true;
    return !projectName.trim();
  }, [
    userId,
    organizationId,
    loadingData,
    isProcessing,
    mode,
    blankProjectName,
    workpillPhase,
    selectedWorkpill,
    projectName,
  ]);

  const handleCreate = async () => {
    if (!userId) {
      toast.error("Not signed in");
      return;
    }
    if (!organizationId) {
      toast.error("Organization not loaded");
      return;
    }

    const name = mode === "blank" ? blankProjectName.trim() : projectName.trim();
    if (!name) {
      toast.error("Enter a project name");
      return;
    }

    if (mode === "workpill" && (workpillPhase === "pick" || !selectedWorkpill)) {
      toast.error("Select a WorkPill");
      return;
    }

    const templateIds = selectedTemplatesWithConfig.map((t) => t.id);
    if (!(await validateSelectedTemplatesHaveFiles(templateIds))) {
      toast.error("One or more templates have no file. Fix in catalog or deselect.");
      return;
    }

    setIsProcessing(true);
    try {
      const execution = await createProjectWithWizardResources({
        userId,
        executionId,
        projectName: name,
        workpillId: mode === "blank" ? null : selectedWorkpill!.id,
        templateConfigs: selectedTemplatesWithConfig,
        knowledgeBaseIds: selectedKnowledgeBaseIds,
        uploadedTemplateFiles,
      });

      const firstDocx = uploadedTemplateFiles.find((f) => f.name.toLowerCase().endsWith(".docx"));
      if (firstDocx) {
        try {
          await WordService.loadDocumentFromBlob(firstDocx);
        } catch {
          /* non-fatal */
        }
      }

      await onCreated(execution);
      toast.success("Project created");
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Create failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const switchMode = (next: CreateWizardInitialMode) => {
    setMode(next);
    setWorkpillPhase(next === "workpill" ? "pick" : "pick");
    setSelectedWorkpillId("");
    setSelectedTemplatesWithConfig([]);
    setSelectedKnowledgeBaseIds([]);
    setUploadedTemplateFiles([]);
    setBlankProjectName("");
    setProjectName("");
    setSearchQuery("");
    lastSeededRef.current = "";
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[440px] max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-3 py-2 border-b shrink-0 space-y-1">
            <div className="flex items-center justify-between gap-2 pr-6">
              <DialogTitle className="text-sm font-semibold">Create project</DialogTitle>
              <Button
                type="button"
                variant={mode === "workpill" ? "default" : "outline"}
                size="sm"
                className="h-7 text-[10px] gap-1 px-2"
                onClick={() => switchMode(mode === "blank" ? "workpill" : "blank")}
              >
                <Zap className="h-3 w-3" />
                {mode === "blank" ? "From WorkPill" : "Blank"}
              </Button>
            </div>
          </DialogHeader>

          <ScrollArea className="flex-1 min-h-0 max-h-[calc(90vh-120px)]">
            <div className="p-3 space-y-3">
              {!userId || !organizationId ? (
                <p className="text-xs text-amber-800">Sign in and wait for organization to load.</p>
              ) : loadingData ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-8 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading…
                </div>
              ) : mode === "blank" ? (
                <WizardResourcesForm
                  projectName={blankProjectName}
                  onProjectNameChange={setBlankProjectName}
                  showProjectName
                  orgTemplates={orgTemplates}
                  orgKbs={orgKbs}
                  selectedTemplates={selectedTemplatesWithConfig}
                  onToggleTemplate={toggleTemplate}
                  selectedKbIds={selectedKnowledgeBaseIds}
                  onToggleKb={toggleKb}
                  uploadedFiles={uploadedTemplateFiles}
                  onUploadedFilesChange={setUploadedTemplateFiles}
                  onOpenTemplateConfig={setTemplateConfigTarget}
                />
              ) : workpillPhase === "pick" ? (
                <div className="space-y-2">
                  <LabelPair text="Search WorkPills" />
                  <Input
                    className="h-9 text-sm"
                    placeholder="Name or description…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <ScrollArea className="h-[280px] border rounded-md bg-white">
                    <div className="p-1 space-y-1">
                      {filteredWorkpills.length === 0 ? (
                        <p className="text-xs text-muted-foreground p-2">No workpills match.</p>
                      ) : (
                        filteredWorkpills.map((wp) => (
                          <button
                            key={wp.id}
                            type="button"
                            className={`w-full text-left rounded-md border px-2 py-2 text-xs transition-colors ${
                              selectedWorkpillId === wp.id
                                ? "border-primary bg-primary/5"
                                : "border-transparent hover:bg-muted/60"
                            }`}
                            onClick={() => setSelectedWorkpillId(wp.id)}
                          >
                            <div className="font-semibold text-foreground truncate">{wp.name}</div>
                            {wp.description ? (
                              <div className="text-muted-foreground line-clamp-2 mt-0.5">{wp.description}</div>
                            ) : null}
                          </button>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                  <Button
                    type="button"
                    className="w-full h-9 text-xs"
                    disabled={!selectedWorkpillId}
                    onClick={() => {
                      const wp = workpills.find((w) => w.id === selectedWorkpillId);
                      if (wp) {
                        lastSeededRef.current = "";
                        seedFromWorkpill(wp);
                        setWorkpillPhase("configure");
                      }
                    }}
                  >
                    Continue
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs px-2"
                      onClick={() => setWorkpillPhase("pick")}
                    >
                      <ChevronLeft className="h-3 w-3 mr-1" />
                      Back
                    </Button>
                    <span className="text-xs text-muted-foreground truncate flex-1">
                      {selectedWorkpill?.name}
                    </span>
                  </div>
                  <WizardResourcesForm
                    projectName={projectName}
                    onProjectNameChange={setProjectName}
                    showProjectName
                    resourcesHint="Resources default from this WorkPill — adjust before creating."
                    orgTemplates={orgTemplates}
                    orgKbs={orgKbs}
                    selectedTemplates={selectedTemplatesWithConfig}
                    onToggleTemplate={toggleTemplate}
                    selectedKbIds={selectedKnowledgeBaseIds}
                    onToggleKb={toggleKb}
                    uploadedFiles={uploadedTemplateFiles}
                    onUploadedFilesChange={setUploadedTemplateFiles}
                    onOpenTemplateConfig={setTemplateConfigTarget}
                  />
                </div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter className="px-3 py-2 border-t shrink-0 flex-row justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs h-8"
              disabled={isProcessing}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            {(mode === "blank" || workpillPhase === "configure") && (
              <Button
                type="button"
                size="sm"
                className="text-xs h-8 gap-1"
                disabled={isCreateDisabled}
                onClick={() => void handleCreate()}
              >
                {isProcessing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Plus className="h-3 w-3" />
                )}
                {isProcessing ? "Creating…" : "Create"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TemplateConfigDialog
        open={templateConfigTarget !== null}
        onOpenChange={(v) => !v && setTemplateConfigTarget(null)}
        organizationId={organizationId}
        template={templateConfigTarget}
        onSave={(next) => {
          setSelectedTemplatesWithConfig((prev) => prev.map((p) => (p.id === next.id ? next : p)));
          setTemplateConfigTarget(null);
        }}
      />
    </>
  );
};

function LabelPair({ text }: { text: string }) {
  return <label className="text-xs font-semibold text-foreground block">{text}</label>;
}
